import React from 'react';
import { alpha, useTheme, Box, Chip, Typography } from '@mui/material';
import { AutoAwesome, CheckRounded } from '@mui/icons-material';
import RulesDemo from './RulesDemo.jsx';

function RulesModal({ gameMode = 'standard4', onTrySample }) {
  const theme = useTheme();

  const rules = {
    standard4: {
      description: [
        'Build from either end of the puzzle and meet in the middle.',
        'Every guess must be a valid word that changes exactly one letter from an open end.',
        'Upper- and lower-colored chains trace each changed-letter path; the outlined tile marks that step.',
        'Gold links show matching positions. Finish with one word that is one letter away from both sides.',
      ],
      example: {
        description:
          'Start with CAME and BATS. CANE extends the top, CATS extends the bottom, and CANS connects both new ends to complete the chain.',
        hint: 'The final move will connect both open ends at once.',
        letterRows: [
          ['c', 'a', 'm', 'e'],
          [null, null, null, null],
          ['b', 'a', 't', 's'],
        ],
      },
    },
    standard5: {
      description: [
        'Build from either end of the puzzle and meet in the middle.',
        'Every guess must be a valid word that changes exactly one letter from an open end.',
        'Upper- and lower-colored chains trace each changed-letter path; the outlined tile marks that step.',
        'Gold links show matching positions. Finish with one word that is one letter away from both sides.',
      ],
      example: {
        description:
          'Start with BAKES and DATES. BIKES extends the top, BATES extends the bottom, and BITES connects both new ends to complete the chain.',
        hint: 'The final move must connect both open ends at once.',
        letterRows: [
          ['b', 'a', 'k', 'e', 's'],
          [null, null, null, null, null],
          ['d', 'a', 't', 'e', 's'],
        ],
      },
    },
  };

  const currentRules = rules[gameMode];
  const { description, hint } = currentRules.example;

  return (
    <Box sx={{ py: 2, px: { xs: 0.25, sm: 1 }, maxWidth: '560px', width: '100%' }}>
      <Box
        sx={{
          p: { xs: 2, sm: 2.5 },
          borderRadius: '20px',
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.14,
          )}, ${alpha(theme.palette.secondary.main, 0.09)})`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
        }}
      >
        <Chip
          icon={<AutoAwesome sx={{ fontSize: '16px !important' }} />}
          label={`${gameMode === 'standard5' ? '5' : '4'} letter mode`}
          size="small"
          sx={{ mb: 1.25, fontWeight: 800, bgcolor: theme.custom.surface }}
        />
        <Typography variant="h6" sx={{ mb: 0.5, lineHeight: 1.2 }}>
          Build a word chain
        </Typography>
        <Typography sx={{ color: theme.custom.mutedText, lineHeight: 1.55 }}>
          {currentRules.description[0]}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 1,
          mt: 1.25,
        }}
      >
        {currentRules.description.slice(1).map((rule, index) => (
          <Box
            key={rule}
            sx={{
              p: 1.5,
              minHeight: { sm: 112 },
              borderRadius: '16px',
              bgcolor: theme.custom.surface,
              border: `1px solid ${theme.custom.border}`,
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                mb: 1,
                display: 'grid',
                placeItems: 'center',
                borderRadius: '9px',
                color:
                  index === 0
                    ? theme.palette.primary.contrastText
                    : index === 2
                    ? theme.palette.secondary.contrastText
                    : theme.palette.text.primary,
                bgcolor: [
                  theme.palette.primary.main,
                  theme.custom.aura,
                  theme.palette.secondary.main,
                ][index],
              }}
            >
              <CheckRounded fontSize="small" />
            </Box>
            <Typography variant="body2" sx={{ color: theme.custom.mutedText, lineHeight: 1.45 }}>
              {rule}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          mt: 1.25,
          p: { xs: 1.5, sm: 2.25 },
          borderRadius: '20px',
          bgcolor: theme.custom.surfaceElevated,
          border: `1px solid ${theme.custom.border}`,
          boxShadow: '0 16px 38px rgba(15, 23, 42, 0.09)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: theme.custom.aura }} />
          <Typography variant="overline" sx={{ fontWeight: 900, letterSpacing: '.12em' }}>
            See it in action
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: theme.custom.mutedText, lineHeight: 1.55 }}>
          {description}
        </Typography>

        <Box sx={{ my: 1.25 }}>
          <RulesDemo gameMode={gameMode} onTrySample={onTrySample} />
        </Box>

        <Typography
          variant="caption"
          sx={{
            textAlign: 'center',
            display: 'block',
            color: theme.custom.mutedText,
            fontWeight: 700,
          }}
        >
          {hint}
        </Typography>
      </Box>
    </Box>
  );
}

export default RulesModal;
